import React from 'react';
import { Gift, Users, Heart, Star, Target, Lightbulb, Sparkles } from 'lucide-react';

const RecipientCategoriesPage = ({ setCurrentPage }) => {
  const recipients = [
    {
      id: 'for-her',
      title: 'Gaver til henne',
      emoji: '👩',
      color: 'pink',
      description: 'Elegant, personlig og gjennomtenkt',
      characteristics: ['Skjønnhet og velvære', 'Opplevelser og minner', 'Personlig stil'],
      age_ranges: ['Tenåringer', 'Unge voksne', 'Familiekvinner', 'Seniorer'],
      typical_interests: ['Mote og skjønnhet', 'Wellness og selvpleie', 'Kultur og kunst', 'Familie og relasjoner']
    },
    {
      id: 'for-him',
      title: 'Gaver til han',
      emoji: '👨',
      color: 'blue',
      description: 'Praktisk, kvalitet og interessebasert',
      characteristics: ['Teknologi og gadgets', 'Sport og fritid', 'Profesjonelt utstyr'],
      age_ranges: ['Tenåringer', 'Unge menn', 'Familiefedre', 'Seniorer'],
      typical_interests: ['Teknologi', 'Sport og aktivitet', 'Biler og motor', 'Håndverk og DIY']
    },
    {
      id: 'for-kids',
      title: 'Gaver til barn',
      emoji: '👶',
      color: 'yellow',
      description: 'Lærerike, sikre og morsomme',
      characteristics: ['Utvikling og læring', 'Kreativitet og lek', 'Sikkerhet først'],
      age_ranges: ['0-2 år', '3-6 år', '7-12 år', '13-17 år'],
      typical_interests: ['Leker og spill', 'Kreativitet', 'Sport og aktivitet', 'Teknologi og gaming']
    },
    {
      id: 'for-teachers',
      title: 'Gaver til lærere',
      emoji: '👩‍🏫',
      color: 'green',
      description: 'Takknemlighet og anerkjennelse',
      characteristics: ['Klasseromsrekvisita', 'Personlig påskjønnelse', 'Praktisk hjelp'],
      age_ranges: ['Alle aldre'],
      typical_interests: ['Utdanning og læring', 'Organisering', 'Selvpleie', 'Klassemiljø']
    },
    {
      id: 'for-teens',
      title: 'Gaver til tenåringer',
      emoji: '🧒',
      color: 'purple',
      description: 'Trendy, personlig og selvstendig',
      characteristics: ['Identitet og uttrykk', 'Teknologi og sosialt', 'Fremtidsrettet'],
      age_ranges: ['13-17 år'],
      typical_interests: ['Mote og stil', 'Teknologi', 'Musikk og kultur', 'Venner og sosialt']
    },
    {
      id: 'for-grandparents',
      title: 'Gaver til besteforeldre',
      emoji: '👴',
      color: 'orange',
      description: 'Komfort, minner og familie',
      characteristics: ['Familie og tradisjoner', 'Komfort og helse', 'Minner og nostalgi'],
      age_ranges: ['60+ år'],
      typical_interests: ['Familie og barnebarn', 'Helse og komfort', 'Hobbyer', 'Minner og historie']
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-accent-50 via-purple-50 to-primary-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👥</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver tilpasset mottakeren</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              De beste gavene er de som viser at du forstår hvem personen er. Utforsk gaver 
              tilpasset ulike mottakere og deres unike behov og interesser.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-accent-500 to-purple-600 text-white rounded-lg hover:from-accent-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn mottaker-spesifikke gaver
            </button>
          </div>
        </div>
      </section>

      {/* Understanding Recipients */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hvorfor mottakeren er nøkkelen til perfekte gaver
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-purple-50 rounded-lg p-6 border border-accent-100 text-center">
                <Target className="w-12 h-12 text-accent-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Personlige preferanser</h3>
                <p className="text-secondary-600">
                  Hver person har unike interesser, behov og ønsker. En gave som treffer deres 
                  personlighet vil alltid verdsettes mer enn noe generisk.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100 text-center">
                <Heart className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Livsfase og behov</h3>
                <p className="text-secondary-600">
                  En tenåring har andre behov enn en bestemor. Ved å forstå deres livsfase 
                  kan du velge gaver som er både relevante og nyttige.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-6 border border-primary-100 text-center">
                <Sparkles className="w-12 h-12 text-primary-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Relasjonen din til dem</h3>
                <p className="text-secondary-600">
                  Hvor godt du kjenner personen påvirker hvilke gaver som er passende. 
                  Nære venner kan få mer personlige gaver enn bekjente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recipients Grid */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk gaver for ulike mottakere
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recipients.map((recipient) => (
                <div key={recipient.id} className="bg-white rounded-lg shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow">
                  <div className={`bg-gradient-to-r from-${recipient.color}-500 to-${recipient.color}-600 p-6 text-center`}>
                    <div className="text-4xl mb-3">{recipient.emoji}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{recipient.title}</h3>
                    <p className="text-white opacity-90 text-sm">{recipient.description}</p>
                  </div>
                  
                  <div className="p-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-700 mb-2">Typiske karakteristikker:</h4>
                        <div className="flex flex-wrap gap-1">
                          {recipient.characteristics.map((char, index) => (
                            <span key={index} className={`text-xs px-2 py-1 bg-${recipient.color}-100 text-${recipient.color}-700 rounded`}>
                              {char}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-700 mb-2">Aldersgrupper:</h4>
                        <p className="text-xs text-secondary-600">{recipient.age_ranges.join(', ')}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-700 mb-2">Populære interesser:</h4>
                        <ul className="text-xs text-secondary-600 space-y-1">
                          {recipient.typical_interests.map((interest, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full mr-2"></span>
                              {interest}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(recipient.id)}
                        className={`w-full mt-4 px-4 py-2 bg-${recipient.color}-500 hover:bg-${recipient.color}-600 text-white rounded-lg transition-colors font-medium text-sm`}
                      >
                        Utforsk {recipient.title.toLowerCase()}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Relationship Considerations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tilpass gaven til din relasjon
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-error-50 to-pink-50 rounded-lg p-6 border border-error-100">
                <Heart className="w-8 h-8 text-error-500 mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Familie</h3>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Mer personlige gaver</li>
                  <li>• Høyere budsjett OK</li>
                  <li>• Kan være intime/følelsesmessige</li>
                  <li>• Familietradisjoner viktig</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Users className="w-8 h-8 text-accent-500 mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Nære venner</h3>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Basert på delte opplevelser</li>
                  <li>• Kan være humoristiske</li>
                  <li>• Reflekter deres hobbyer</li>
                  <li>• Medium budsjett</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Star className="w-8 h-8 text-success-500 mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Kolleger</h3>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Profesjonell og passende</li>
                  <li>• Ikke for personlig</li>
                  <li>• Lavere budsjett</li>
                  <li>• Universelt akseptable</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Lightbulb className="w-8 h-8 text-purple-500 mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Bekjente</h3>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Enkle og trygge valg</li>
                  <li>• Gavekort fungerer godt</li>
                  <li>• Unngå kontroversielle emner</li>
                  <li>• Beskjedent budsjett</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Age Considerations */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Alderstilpassede gavehensyn
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">👶</span>
                  <h3 className="text-xl font-semibold text-secondary-800">Barn og unge (0-17 år)</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Fokusområder:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Sikkerhet og alderstilpasset</li>
                      <li>• Læring og utvikling</li>
                      <li>• Kreativitet og lek</li>
                      <li>• Fremtidig nytte</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Unngå:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• For avanserte produkter</li>
                      <li>• Små deler (under 3 år)</li>
                      <li>• Voldelig eller skummelt innhold</li>
                      <li>• Alt foreldrene må vedlikeholde</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">👩‍💼</span>
                  <h3 className="text-xl font-semibold text-secondary-800">Voksne (18-65 år)</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Fokusområder:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Livsstil og interesser</li>
                      <li>• Karriere og profesjonell utvikling</li>
                      <li>• Familie og relasjoner</li>
                      <li>• Kvalitet over kvantitet</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Vurder:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Deres livsfase (student, ny forelder, etc.)</li>
                      <li>• Tid tilgjengelig</li>
                      <li>• Praktiske behov</li>
                      <li>• Langsiktig verdi</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">👴</span>
                  <h3 className="text-xl font-semibold text-secondary-800">Seniorer (65+ år)</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Fokusområder:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Komfort og tilgjengelighet</li>
                      <li>• Minner og nostalgi</li>
                      <li>• Familie og tradisjoner</li>
                      <li>• Helse og velvære</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Praktiske hensyn:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Store knapper og tydelig tekst</li>
                      <li>• Enkelt å bruke og vedlikeholde</li>
                      <li>• Ikke for komplisert teknologi</li>
                      <li>• Fokus på kvalitetstid</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-accent-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte mottaker-spesifikke gaven?
            </h2>
            <p className="text-xl text-accent-100 mb-8">
              La oss hjelpe deg å velge noe som virkelig passer til personen du vil glede
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-accent-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('categories')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-accent-600 transition-colors font-semibold"
              >
                Se alle kategorier
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecipientCategoriesPage;